import { useState, useEffect } from "react";
import { Check, AlertCircle, Loader2, Luggage, Plane } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/hooks/use-analytics";
import FlightStatusDialog from "@/components/FlightStatusDialog";
import { getFlightStatus } from "@/utils/flightStatus";
import { format, parseISO } from 'date-fns';
import { useToast } from "@/components/ui/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

// Create a global event bus for button coordination
export const buttonEvents = {
  checkInExpanded: new CustomEvent('checkInExpanded'),
  statusExpanded: new CustomEvent('statusExpanded'),
  eTicketExpanded: new CustomEvent('eTicketExpanded'),
  allCollapsed: new CustomEvent('allCollapsed')
};

interface FlightActionButtonsProps {
  flightNumber: string;
  isWithin72Hours: boolean;
  isWithin48Hours: boolean;
  checkinUrl: string | { url: string; windowOpen: boolean; hoursUntilOpen: number } | null;
  onStatusClick: (e: React.MouseEvent) => void;
  tripId: string;
  destinationName: string;
  departureDate: string;
  departureCode: string;
  arrivalCode?: string;
  confirmationNumbers?: {
    ticket: string;
    isDownloadable: boolean;
  }[];
}

const FlightActionButtons = ({
  flightNumber,
  isWithin72Hours,
  isWithin48Hours,
  checkinUrl,
  onStatusClick,
  tripId,
  destinationName,
  departureDate,
  departureCode,
  arrivalCode = "",
  confirmationNumbers = []
}: FlightActionButtonsProps) => {
  const { t } = useTranslation();
  const { trackTripCardAction } = useAnalytics();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [expandCheckIn, setExpandCheckIn] = useState(false);
  const [expandStatus, setExpandStatus] = useState(false);
  const [showFlightStatusDialog, setShowFlightStatusDialog] = useState(false);
  const [flightStatus, setFlightStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const handleCheckInClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    console.debug(`🖱️ Check-in button clicked for flight ${flightNumber}`);
    trackTripCardAction("check_in", tripId, destinationName);
    
    // checkinUrl is now an object with url, windowOpen and hoursUntilOpen properties
    if (typeof checkinUrl === 'object' && checkinUrl !== null) {
      if (checkinUrl.windowOpen && checkinUrl.url) {
        console.debug(`🔗 Opening check-in URL: ${checkinUrl.url}`);
        window.open(checkinUrl.url, "_blank");
      } else if (checkinUrl.url) {
        // Check-in URL exists but window is not open yet
        const hoursLeft = Math.ceil(checkinUrl.hoursUntilOpen);
        const daysLeft = Math.floor(hoursLeft / 24);
        const remainingHours = hoursLeft % 24;
        
        let timeMessage = "";
        if (daysLeft > 0) {
          timeMessage = `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`;
          if (remainingHours > 0) {
            timeMessage += ` and ${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'}`;
          }
        } else {
          timeMessage = `${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'}`;
        }
        
        toast({
          title: t("tripCard.checkinNotAvailable", "Check-in Not Available"),
          description: t("tripCard.checkinWindowClosedWithTime", "Online check-in will be available in {{time}}. Please try again later.", { time: timeMessage }),
          variant: "default"
        });
      } else {
        // No check-in URL available
        toast({
          title: t("tripCard.checkinNotAvailable", "Check-in Not Available"),
          description: t("tripCard.automaticCheckin", "Automatic check-in will be performed for you."),
          variant: "default"
        });
      }
    } else if (typeof checkinUrl === 'string' && checkinUrl) {
      // Handle legacy string URL for backward compatibility
      console.debug(`🔗 Opening legacy check-in URL: ${checkinUrl}`);
      window.open(checkinUrl, "_blank");
    } else {
      console.warn("No check-in URL available");
      toast({
        title: t("tripCard.checkinNotAvailable", "Check-in Not Available"),
        description: t("tripCard.automaticCheckin", "Automatic check-in will be performed for you."),
        variant: "default"
      });
    }
  };
  
  const handleStatusClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.debug(`Status button clicked for flight ${flightNumber}`, { departureCode, arrivalCode });
    
    trackTripCardAction("flight_status", tripId, destinationName);
    
    // Only fetch data if we don't have it already
    if (!flightStatus && !loading) {
      setLoading(true);
      try {
        const formattedDate = format(parseISO(departureDate || "1970"), 'yyyy-MM-dd');
        // Extract just the flight number without airline code (e.g., "565" from "EK565")
        const numericFlightNumber = flightNumber.replace(/^[A-Z]+/, '');
        const formattedFlightId = `${flightNumber}_${departureCode}_${formattedDate}`;
        console.debug(`Fetching flight status for: ${formattedFlightId}`);
        const statusData = await getFlightStatus([formattedFlightId]);
        if (statusData.length > 0) {
          setFlightStatus(statusData[0]);
          setStatusError(null);
        } else {
          setStatusError("No data found for this flight");
        }
      } catch (error) {
        setStatusError("Failed to fetch flight status");
      } finally {
        setLoading(false);
      }
    }
    
    // Show dialog after attempting to fetch (or using cached data)
    setShowFlightStatusDialog(true);
    console.debug(`Setting showFlightStatusDialog to true:`, showFlightStatusDialog);
    
    onStatusClick(e);
  };

  useEffect(() => {
    // Listen for events from other buttons
    const handleETicketExpanded = () => {
      setExpandCheckIn(false);
      setExpandStatus(false);
    };
    
    // Add event listeners
    document.addEventListener('eTicketExpanded', handleETicketExpanded);
    
    // Clean up
    return () => {
      document.removeEventListener('eTicketExpanded', handleETicketExpanded);
    };
  }, []);

  const handleCheckInMouseEnter = () => {
    setExpandCheckIn(true);
    // Dispatch event to notify other buttons
    document.dispatchEvent(buttonEvents.checkInExpanded);
    
    // Auto-hide after 0.5 seconds
    setTimeout(() => {
      setExpandCheckIn(false);
    }, 500);
  };

  const handleStatusMouseEnter = () => {
    setExpandStatus(true);
    // Dispatch event to notify other buttons
    document.dispatchEvent(buttonEvents.statusExpanded);
    
    // Auto-hide after 0.5 seconds
    setTimeout(() => {
      setExpandStatus(false);
    }, 500);
  };

  const handleMouseLeave = () => {
    setExpandCheckIn(false);
    setExpandStatus(false);
    // Dispatch event to notify other buttons
    document.dispatchEvent(buttonEvents.allCollapsed);
  };

  // Log when dialog state changes
  useEffect(() => {
    console.debug(`Dialog state changed to: ${showFlightStatusDialog}`);
  }, [showFlightStatusDialog]);

  // Debug confirmation numbers
  useEffect(() => {
    console.debug('Confirmation numbers:', confirmationNumbers);
  }, [confirmationNumbers]);

  return (
    <div className="flex gap-2">
      {(isWithin72Hours || (typeof checkinUrl === 'object' && checkinUrl?.url)) && (
        <Button 
          size="sm" 
          variant="default" 
          className={`bg-blue-600 hover:bg-blue-700 text-white text-xs py-1 ${expandCheckIn ? 'px-2' : 'px-1'} h-7 rounded shadow-sm flex items-center gap-1 transition-all duration-200`}
          onClick={handleCheckInClick}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={handleCheckInMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleCheckInMouseEnter}
        >
          {isMobile ? (
            <>
              <Luggage className="w-5 h-5" />
              {expandCheckIn && <span className="ml-1">{t("tripCard.checkIn", "Check-In")}</span>}
            </>
          ) : (
            <>
              <Luggage className="w-5 h-5 mr-1" />
              {t("tripCard.checkIn", "Check-In")}
            </>
          )}
        </Button>
      )}
      {/* Status button removed as requested */}
      
      {/* Flight Status Dialog - Keep this for when status is shown in other ways */}
      {showFlightStatusDialog && (
        <FlightStatusDialog
          open={showFlightStatusDialog}
          onOpenChange={(open) => setShowFlightStatusDialog(open)}
          flightNumber={flightNumber}
          departureCode={departureCode}
          arrivalCode={arrivalCode || ""}
          scheduledDeparture={departureDate}
          flightStatus={flightStatus}
          loading={loading}
          statusError={statusError}
        />
      )}
    </div>
  );
};

export default FlightActionButtons;
